import { z } from 'zod';
import { CustomFieldType, DateOnly, HexColor, Id, atLeastOneField } from './common.js';

export const CustomFieldAppliesTo = z.enum(['asset', 'board']);
export type CustomFieldAppliesTo = z.infer<typeof CustomFieldAppliesTo>;

export const CustomFieldOption = z.object({
  id: Id,
  label: z.string().max(120),
  color: HexColor.nullable().optional(),
});
export type CustomFieldOption = z.infer<typeof CustomFieldOption>;

export const CustomField = z.object({
  id: Id,
  workspace_id: Id,
  name: z.string().max(120),
  key: z.string().regex(/^[a-z0-9_]{1,64}$/),
  type: CustomFieldType,
  options: z.array(CustomFieldOption).optional(),
  required: z.boolean(),
  applies_to: CustomFieldAppliesTo,
  sort_order: z.int(),
});
export type CustomField = z.infer<typeof CustomField>;

export const CustomFieldCreate = z.object({
  name: z.string().min(1).max(120),
  key: z
    .string()
    .regex(/^[a-z0-9_]{1,64}$/)
    .optional(),
  type: CustomFieldType,
  options: z
    .array(
      z.object({
        label: z.string().max(120),
        color: HexColor.optional(),
      }),
    )
    .optional(),
  required: z.boolean().default(false),
  applies_to: CustomFieldAppliesTo,
  sort_order: z.int().optional(),
});
export type CustomFieldCreate = z.infer<typeof CustomFieldCreate>;

export const CustomFieldUpdate = atLeastOneField(
  z.object({
    name: z.string().min(1).max(120).optional(),
    options: z
      .array(
        z.object({
          id: Id.optional(),
          label: z.string().max(120),
          color: HexColor.nullable().optional(),
        }),
      )
      .optional(),
    required: z.boolean().optional(),
    sort_order: z.int().optional(),
  }),
);
export type CustomFieldUpdate = z.infer<typeof CustomFieldUpdate>;

export const CustomFieldValue = z.object({
  id: Id,
  field_id: Id,
  field_key: z.string().optional(),
  asset_id: Id.nullable().optional(),
  board_id: Id.nullable().optional(),
  value_text: z.string().nullable().optional(),
  value_number: z.number().nullable().optional(),
  value_date: DateOnly.nullable().optional(),
  value_option_ids: z.array(Id).nullable().optional(),
});
export type CustomFieldValue = z.infer<typeof CustomFieldValue>;

export const CustomFieldValueSet = z.object({
  value_text: z.string().max(10000).nullable().optional(),
  value_number: z.number().nullable().optional(),
  value_date: DateOnly.nullable().optional(),
  value_option_ids: z.array(Id).max(100).nullable().optional(),
});
export type CustomFieldValueSet = z.infer<typeof CustomFieldValueSet>;
